import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

import type { Locale } from 'date-fns';

type DateFormatKey = 'long' | 'medium' | 'short';

/**
 * Formatea una fecha al formato 'dd/MM/yyyy'.
 * @param date - La fecha a formatear (puede ser una cadena o un objeto Date).
 * @param fallback - Texto que se muestra si la fecha no es válida.
 * @returns La fecha formateada o el texto de fallback.
 */
export const formatDate = (date: string | Date, fallback: string = 'Invalid Date'): string => {
  try {
    return format(new Date(date), 'dd/MM/yyyy');
  } catch (error) {
    return fallback;
  }
};

const getFormatDate = (value?: string | Date): string => {

  // El original reasignaba el parametro cambiandole el tipo; en TS se resuelve a una
  // variable local con el tipo ya estrechado. Mismo resultado.
  let resolved: Date;
  if (!value) resolved = new Date();
  else if (typeof value === "string") resolved = new Date(value);
  else resolved = value;
  const currentDate = resolved;

  const day = currentDate.getDate().toString().padStart(2, "0");
  const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");
  const year = currentDate.getFullYear().toString();
  const hours = currentDate.getHours().toString().padStart(2, "0");
  const minutes = currentDate.getMinutes().toString().padStart(2, "0");
  const seconds = currentDate.getSeconds().toString().padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export { getFormatDate }

/**
 * Formatea una fecha a un formato legible según la localización especificada.
 *
 * @param date - La fecha a formatear. Si es una cadena, se convierte a objeto Date.
 * @param locale - La localización a utilizar ('es' para español, 'en' para inglés).
 * @returns La fecha formateada o 'Fecha inválida' si la fecha no es válida.
 */
export const humanReadableTime = (date?: string | Date, locale: string = 'es'): string => {
  try {
    let resolved: Date;
    if (!date) resolved = new Date();
    else if (typeof date === "string") resolved = new Date(date);
    else resolved = date;

    // `isNaN(fecha)` coercionaba via valueOf(); en TS se pide el timestamp explicito.
    if (isNaN(resolved.getTime())) throw new Error('Fecha inválida');

    const locales: Record<string, Locale> = { es, en: enUS };
    const selectedLocale = locales[locale] || enUS;

    // Formato deseado: 'Wed, 18 Dec 2024' o 'Mié, 18 Dic 2024'
    return format(resolved, 'EEE, dd MMM yyyy', { locale: selectedLocale });
  } catch (error) {
    console.error(error);
    return 'Fecha inválida';
  }
};
/**
 * Formatea una fecha a un formato legible según la localización especificada.
 *
 * @param dateString - La fecha a formatear.
 * @param format - El formato a utilizar ('long' para el formato completo, 'medium' para el formato corto, 'short' para el formato corto).
 * @param language - La localización a utilizar ('es' para español, 'en' para inglés).
 * @returns La fecha formateada o 'Fecha inválida' si la fecha no es válida.
 */
export const formatDateWithIntl = (dateString: string, format: DateFormatKey = 'long', language: string = 'es'): string => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        const formats: Record<DateFormatKey, Intl.DateTimeFormatOptions> = {
            long: {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            },
            medium: {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            },
            short: {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }
        };

        return new Intl.DateTimeFormat(language, formats[format]).format(date);
    } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
    }
};
